import {
  afterNextRender,
  ApplicationRef,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  createComponent,
  ElementRef,
  EnvironmentInjector,
  EventEmitter,
  Injector,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
} from "@angular/core";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { DisclosureDto } from "../../agreement/agreement.data";
import { AgreementDisclosureComponent } from "../agreement-disclosure/agreement-disclosure.component";
import { AgreementDisclosureProgress } from "../domain/models/agreement-disclosure.model";

interface PendingTarget {
  code: string;
  element: HTMLElement | null;
}

/** A disclosure placed in the agreement: its component, and the paragraph it colours. */
interface MountedDisclosure {
  ref: ComponentRef<AgreementDisclosureComponent>;
  paragraph: HTMLElement;
}

const DisclosureTextClass = "agreement-document__disclosure-text";
const DisclosureTextAcceptedClass = `${DisclosureTextClass}--accepted`;

/**
 * The in-house agreement. The paragraph whose id is a disclosure code is coloured, and the disclosure is mounted
 * right after it.
 */
@UntilDestroy()
@Component({
  selector: "ifp-agreement-document",
  templateUrl: "./agreement-document.component.html",
  styleUrls: ["./agreement-document.component.scss"],
  standalone: false,
})
export class AgreementDocumentComponent implements OnChanges, OnDestroy {
  @Input() html: string;

  @Input() disclosures: DisclosureDto[];

  @Input() signerInitials: string;

  /** Asked before every acceptance; false stops it, the guard reporting why. */
  @Input() acceptGuard: () => boolean;

  @Output() progressChange = new EventEmitter<AgreementDisclosureProgress>();

  /** Emits when the signer accepts the last pending disclosure. */
  @Output() completed = new EventEmitter<void>();

  @ViewChild("content", { static: true }) content: ElementRef<HTMLElement>;

  /** The fallback list below the agreement; the slots mounted into it are in {@link mounted}. */
  @ViewChildren(AgreementDisclosureComponent) unplacedComponents: QueryList<AgreementDisclosureComponent>;

  safeHtml: SafeHtml;

  /** Pending disclosures with pop-up text but no slot in the agreement, offered below it instead. */
  unplacedDisclosures: DisclosureDto[] = [];

  /** A pending disclosure meant to be initialled in place has no slot, so it can never be accepted here. */
  missingPlacement = false;

  /** Each disclosure's place among the agreement's, by code, for the pop-up's "1 of 4". */
  positions: Record<string, number> = {};

  private readonly pendingCodes = new Set<string>();

  private mounted: MountedDisclosure[] = [];

  constructor(
    private readonly sanitizer: DomSanitizer,
    private readonly appRef: ApplicationRef,
    private readonly environmentInjector: EnvironmentInjector,
    private readonly injector: Injector,
    private readonly hostElement: ElementRef<HTMLElement>,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly zone: NgZone
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.html) {
      this.safeHtml = this.sanitizer.bypassSecurityTrustHtml(this.html ?? "");
    }

    // The slots exist only once the new html is in the DOM. Render hooks run outside the zone, and a component
    // created there would handle its clicks and responses outside it too, never refreshing its view.
    afterNextRender(() => this.zone.run(() => this.mountDisclosures()), { injector: this.injector });
  }

  /** Brings the first pending disclosure into view and highlights it; false when none is pending. */
  scrollToFirstPending(): boolean {
    return this.scrollTo(this.pendingTargets()[0]);
  }

  /** Moves the signer on: to the next pending disclosure after the one just accepted, or past the last to Accept. */
  onAccepted(code: string): void {
    this.pendingCodes.delete(code);
    this.mounted
      .find((mounted) => mounted.ref.instance.disclosure?.code === code)
      ?.paragraph.classList.add(DisclosureTextAcceptedClass);
    this.progressChange.emit({ pendingCount: this.pendingCodes.size });

    if (!this.scrollToPendingAfter(code)) this.completed.emit();
  }

  /** One disclosure pop-up at a time, as before. */
  onPopupOpened(code: string): void {
    this.allDisclosureComponents()
      .filter((component) => component.disclosure?.code !== code)
      .forEach((component) => component.closePopup());
  }

  trackByCode(_index: number, disclosure: DisclosureDto): string {
    return disclosure.code;
  }

  private mountDisclosures(): void {
    // Nothing is known before the agreement arrives; reporting "none pending" then would let Accept through.
    if (!this.disclosures) return;

    this.unmount();
    this.pendingCodes.clear();

    const unplaced: DisclosureDto[] = [];
    let missingPlacement = false;
    this.positions = Object.fromEntries((this.disclosures ?? []).map((disclosure, index) => [disclosure.code, index + 1]));

    for (const disclosure of this.disclosures ?? []) {
      if (!disclosure.acceptedOnUtc) this.pendingCodes.add(disclosure.code);

      const paragraph = this.findById(this.content.nativeElement, disclosure.code);
      if (paragraph) {
        this.mount(disclosure, paragraph);
      } else if (!disclosure.acceptedOnUtc) {
        if (disclosure.text?.trim()) unplaced.push(disclosure);
        else missingPlacement = true;
      }
    }

    this.unplacedDisclosures = unplaced;
    this.missingPlacement = missingPlacement;
    this.progressChange.emit({ pendingCount: this.pendingCodes.size });
    this.changeDetectorRef.markForCheck();
  }

  /** Colours the disclosure's paragraph and mounts the disclosure into a slot right after it; the paragraph keeps its text. */
  private mount(disclosure: DisclosureDto, paragraph: HTMLElement): void {
    paragraph.classList.add(DisclosureTextClass);
    paragraph.classList.toggle(DisclosureTextAcceptedClass, !!disclosure.acceptedOnUtc);

    const slot = paragraph.ownerDocument.createElement("div");
    paragraph.insertAdjacentElement("afterend", slot);

    const ref = createComponent(AgreementDisclosureComponent, {
      environmentInjector: this.environmentInjector,
      elementInjector: this.injector,
      hostElement: slot,
    });

    ref.setInput("disclosure", disclosure);
    ref.setInput("signerInitials", this.signerInitials);
    ref.setInput("acceptGuard", this.acceptGuard);
    ref.setInput("position", this.positions[disclosure.code]);
    ref.setInput("total", this.disclosures.length);
    ref.instance.accepted.pipe(untilDestroyed(this)).subscribe((code) => this.onAccepted(code));
    ref.instance.popupOpened.pipe(untilDestroyed(this)).subscribe((code) => this.onPopupOpened(code));

    this.appRef.attachView(ref.hostView);
    ref.changeDetectorRef.detectChanges();
    this.mounted.push({ ref, paragraph });
  }

  /** Pending disclosures in page order; one with no slot points at the error that explains why. */
  private pendingTargets(): PendingTarget[] {
    const host = this.hostElement.nativeElement;
    const error = host.querySelector<HTMLElement>(".agreement-document__error");

    return (this.disclosures ?? [])
      .filter((disclosure) => this.pendingCodes.has(disclosure.code))
      .map((disclosure) => ({ code: disclosure.code, element: this.findById(host, disclosure.code) ?? error }))
      .sort((a, b) => (a.element?.getBoundingClientRect().top ?? Infinity) - (b.element?.getBoundingClientRect().top ?? Infinity));
  }

  /** The next pending disclosure below the given one, wrapping to the first; false when none is pending. */
  private scrollToPendingAfter(code: string): boolean {
    const targets = this.pendingTargets();
    const acceptedTop = this.findById(this.hostElement.nativeElement, code)?.getBoundingClientRect().top ?? -Infinity;

    return this.scrollTo(targets.find((target) => (target.element?.getBoundingClientRect().top ?? -Infinity) > acceptedTop) ?? targets[0]);
  }

  private scrollTo(target: PendingTarget | undefined): boolean {
    if (!target) return false;

    target.element?.scrollIntoView({ block: "center", behavior: "smooth" });

    this.allDisclosureComponents()
      .find((component) => component.disclosure?.code === target.code)
      ?.focus();

    return true;
  }

  private allDisclosureComponents(): AgreementDisclosureComponent[] {
    return [...this.mounted.map((mounted) => mounted.ref.instance), ...(this.unplacedComponents?.toArray() ?? [])];
  }

  /** By id attribute rather than a selector, as disclosure codes are not guaranteed to be valid CSS identifiers. */
  private findById(root: HTMLElement, id: string): HTMLElement | null {
    return Array.from(root.querySelectorAll<HTMLElement>("[id]")).find((element) => element.id === id) ?? null;
  }

  /** Leaves the agreement as it arrived: destroying a disclosure also takes its slot out of the page. */
  private unmount(): void {
    this.mounted.forEach(({ ref, paragraph }) => {
      ref.destroy();
      paragraph.classList.remove(DisclosureTextClass, DisclosureTextAcceptedClass);
    });
    this.mounted = [];
  }

  ngOnDestroy(): void {
    this.unmount();
  }
}
